export type PaginationItem = { type: "page"; page: number } | { type: "ellipsis" };

/**
 * 智能分页项生成（首尾各保留 1 页 + 中间 siblingCount）。
 *
 * 抽出到 `lib/utils` 供客户端 `components/custom/Pagination`（回调式）与
 * 服务端 `components/custom/blog/BlogPagination`（链接式）共用，避免两处
 * 算法分叉。`siblingCount` / `totalNumbers` 为实现细节，不导出。
 */
export function getPaginationItems(
  current: number,
  total: number,
): PaginationItem[] {
  const siblingCount = 1;
  const totalNumbers = siblingCount * 2 + 5;
  const range = (start: number, end: number) =>
    Array.from({ length: end - start + 1 }, (_, idx) => start + idx);

  if (total <= totalNumbers) {
    return range(1, total).map((page) => ({ type: "page" as const, page }));
  }

  const leftSiblingIndex = Math.max(current - siblingCount, 1);
  const rightSiblingIndex = Math.min(current + siblingCount, total);
  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < total - 1;
  const items: PaginationItem[] = [];
  const addPageRange = (start: number, end: number) => {
    range(start, end).forEach((page) => items.push({ type: "page", page }));
  };

  if (!shouldShowLeftDots && shouldShowRightDots) {
    addPageRange(1, 3 + siblingCount * 2);
    items.push({ type: "ellipsis" }, { type: "page", page: total });
    return items;
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    addPageRange(total - (3 + siblingCount * 2) + 1, total);
    items.unshift({ type: "page", page: 1 }, { type: "ellipsis" });
    return items;
  }

  addPageRange(1, 1);
  items.push({ type: "ellipsis" });
  addPageRange(leftSiblingIndex, rightSiblingIndex);
  items.push({ type: "ellipsis" }, { type: "page", page: total });
  return items;
}
